import { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area, CartesianGrid, Legend,
} from 'recharts';
import { useAuthFetch } from '../hooks/useAuthFetch';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg: '#0f1117', card: '#1a1d2e', border: '#2a2d3e',
  text: '#e2e8f0', muted: '#94a3b8', faint: '#475569',
  accent: '#41B073', green: '#22c55e', red: '#ef4444',
  orange: '#f59e0b', blue: '#3b82f6', cyan: '#06b6d4',
};

const fmt  = n => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtD = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const is   = { background: C.card, border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };

const CATEG_COLORS = {
  'Honoraires conseil':    '#41B073', 'Honoraires formation': '#8b5cf6',
  'Remboursements':        '#3b82f6', 'Autres recettes':       '#22c55e',
  'Logiciels & SaaS':      '#06b6d4', 'Hébergement & Infra':  '#0ea5e9',
  'Déplacements':          '#f59e0b', 'Sous-traitance':        '#a855f7',
  'Marketing & Comm.':     '#ec4899', 'Banque & Frais':        '#94a3b8',
  'Cotisations & Taxes':   '#ef4444', 'Matériel & Équip.':     '#f97316',
  'Assurance':             '#64748b', 'Autres dépenses':       '#475569',
};

const Badge = ({ text, color }) => (
  <span style={{ background: (color || C.muted) + '22', color: color || C.muted, border: `1px solid ${(color || C.muted)}44`, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{text}</span>
);

const KPI = ({ label, value, sub, color, icon }) => (
  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px', minWidth: 0 }}>
    <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon && <span>{icon}</span>}{label}
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color: color || C.text, fontFamily: 'DM Mono, monospace' }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.faint, marginTop: 3 }}>{sub}</div>}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#0d0f1a', border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
      <div style={{ color: C.muted, marginBottom: 6, fontWeight: 600 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.fill || p.stroke, marginBottom: 2 }}>
          {p.name} : {fmt(p.value)}
        </div>
      ))}
    </div>
  );
};

// ─── Onglet Dashboard ─────────────────────────────────────────────────────────
function TabDashboard({ fetchWithAuth }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchWithAuth('/api/comptabilite/dashboard').then(r => r.json()).then(setData);
  }, []);

  const doRapprochement = async () => {
    const r = await fetchWithAuth('/api/comptabilite/rapprochement', { method: 'POST' });
    const d = await r.json();
    alert(`Rapprochement terminé : ${d.matched} facture(s) rapprochée(s)`);
    fetchWithAuth('/api/comptabilite/dashboard').then(r => r.json()).then(setData);
  };

  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Chargement…</div>;

  const categData = Object.entries(data.by_category || {})
    .sort((a, b) => b[1] - a[1]).slice(0, 8)
    .map(([cat, val]) => ({ cat: cat.length > 18 ? cat.slice(0, 17) + '…' : cat, val: Math.round(val), color: CATEG_COLORS[cat] || C.muted }));

  return (
    <div>
      {/* KPIs ligne 1 */}
      <div className="kpi-grid" style={{ marginBottom: 12 }}>
        <KPI icon="🏦" label="Trésorerie estimée" value={fmt(data.tresorerie)} color={data.tresorerie >= 0 ? C.green : C.red} sub="Cumul toutes transactions" />
        <KPI icon="📈" label={`CA ${new Date().toLocaleDateString('fr-FR', { month: 'long' })}`} value={fmt(data.recettes_mois)} color={C.green} sub={`YTD : ${fmt(data.ca_ytd)}`} />
        <KPI icon="📉" label="Dépenses mois" value={fmt(data.depenses_mois)} color={C.red} sub={`YTD : ${fmt(data.charges_ytd)}`} />
        <KPI icon="💰" label="Résultat mois" value={fmt(data.recettes_mois - data.depenses_mois)} color={(data.recettes_mois - data.depenses_mois) >= 0 ? C.green : C.red} sub={`YTD : ${fmt(data.ca_ytd - data.charges_ytd)}`} />
      </div>

      {/* KPIs ligne 2 */}
      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <KPI icon="🧾" label="Factures en attente" value={data.factures_attente} color={data.factures_attente > 0 ? C.orange : C.muted} sub={`Montant : ${fmt(data.montant_attente)}`} />
        <KPI icon="🔗" label="À rapprocher" value={data.non_rapprochees} color={data.non_rapprochees > 0 ? C.orange : C.muted} sub={<button onClick={doRapprochement} style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', padding: 0 }}>Lancer le rapprochement →</button>} />
        <KPI icon="🟢" label="TVA collectée" value={fmt(data.tva_collectee)} color={C.cyan} sub="mois courant" />
        <KPI icon="🔵" label="TVA nette à reverser" value={fmt(data.tva_nette)} color={data.tva_nette > 0 ? C.orange : C.green} sub={`Déduc. : ${fmt(data.tva_deductible)}`} />
      </div>

      {/* Graphique 12 mois */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '18px 20px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 16 }}>Recettes & Dépenses — 12 mois glissants</div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.monthly} barGap={2} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="label" tick={{ fill: C.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: C.muted, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${Math.round(v / 1000)}k`} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, color: C.muted }} />
            <Bar dataKey="recettes" name="Recettes" fill={C.green} radius={[3, 3, 0, 0]} opacity={0.85} />
            <Bar dataKey="depenses" name="Dépenses" fill={C.red}   radius={[3, 3, 0, 0]} opacity={0.75} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Répartition dépenses */}
      {categData.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '18px 20px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 16 }}>Répartition dépenses YTD par catégorie</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {categData.map(({ cat, val, color }) => {
              const pct = Math.round(val / categData.reduce((s, d) => s + d.val, 0) * 100);
              return (
                <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 130, fontSize: 12, color: C.muted, flexShrink: 0 }}>{cat}</div>
                  <div style={{ flex: 1, height: 6, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: color || C.accent, borderRadius: 3 }} />
                  </div>
                  <div style={{ width: 80, fontSize: 12, color: C.text, fontFamily: 'DM Mono, monospace', textAlign: 'right', flexShrink: 0 }}>{fmt(val)}</div>
                  <div style={{ width: 32, fontSize: 11, color: C.faint, flexShrink: 0 }}>{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Transactions ──────────────────────────────────────────────────────
function TxnModal({ txn, categories, onSave, onClose }) {
  const [form, setForm] = useState(txn || {
    label: '', amount: '', settled_at: new Date().toISOString().slice(0, 10),
    category: '', note: '', vat_rate: 0,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isRecette = parseFloat(form.amount) > 0;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200 }} />
      <div className="modal-scroll" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 460, maxHeight: '85vh', overflowY: 'auto', background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, zIndex: 201 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ margin: 0, color: C.text, fontSize: 16 }}>{txn ? 'Modifier la transaction' : 'Nouvelle transaction'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>

        {[['label', 'Libellé *'], ['note', 'Note']].map(([k, l]) => (
          <div key={k} style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>{l}</label>
            <input value={form[k] || ''} onChange={e => set(k, e.target.value)} style={is} />
          </div>
        ))}

        <div className="form-grid" style={{ marginBottom: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>Montant € *<span style={{ color: C.faint, fontWeight: 400 }}> (+ recette, - dépense)</span></label>
            <input type="number" step="0.01" value={form.amount || ''} onChange={e => set('amount', e.target.value)} style={{ ...is, color: isRecette ? C.green : C.red }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>Date</label>
            <input type="date" value={form.settled_at || ''} onChange={e => set('settled_at', e.target.value)} style={{ ...is, colorScheme: 'dark' }} />
          </div>
        </div>

        <div className="form-grid" style={{ marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>Catégorie</label>
            <select value={form.category || ''} onChange={e => set('category', e.target.value)} style={{ ...is, cursor: 'pointer' }}>
              <option value="">— Auto —</option>
              <optgroup label="Recettes">{(categories.recettes || []).map(c => <option key={c}>{c}</option>)}</optgroup>
              <optgroup label="Dépenses">{(categories.depenses || []).map(c => <option key={c}>{c}</option>)}</optgroup>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>Taux TVA %</label>
            <select value={String(form.vat_rate || 0)} onChange={e => set('vat_rate', e.target.value)} style={{ ...is, cursor: 'pointer' }}>
              {[0, 5.5, 10, 20].map(r => <option key={r} value={r}>{r} %</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onSave(form)} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: C.accent, border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit' }}>
            Enregistrer
          </button>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, background: 'transparent', border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Annuler</button>
        </div>
      </div>
    </>
  );
}

function TabTransactions({ fetchWithAuth }) {
  const [txns, setTxns]       = useState([]);
  const [categories, setCats] = useState({});
  const [filters, setFilters] = useState({ type: '', category: '', search: '', annee: String(new Date().getFullYear()) });
  const [modal, setModal]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filters.annee)    p.set('annee',    filters.annee);
    if (filters.type)     p.set('type',     filters.type);
    if (filters.category) p.set('category', filters.category);
    if (filters.search)   p.set('search',   filters.search);
    fetchWithAuth(`/api/comptabilite/transactions?${p}`).then(r => r.json()).then(d => { setTxns(d); setLoading(false); });
  };

  useEffect(() => {
    fetchWithAuth('/api/comptabilite/categories').then(r => r.json()).then(setCats);
  }, []);

  useEffect(() => { load(); }, [filters.annee, filters.type, filters.category]);

  const handleSearch = e => { if (e.key === 'Enter') load(); };

  const save = async (form) => {
    const method = modal?.id ? 'PUT' : 'POST';
    const url    = modal?.id ? `/api/comptabilite/transactions/${modal.id}` : '/api/comptabilite/transactions';
    await fetchWithAuth(url, { method, body: JSON.stringify(form) });
    setModal(null); load();
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette transaction ?')) return;
    await fetchWithAuth(`/api/comptabilite/transactions/${id}`, { method: 'DELETE' });
    load();
  };

  const exportCSV = () => {
    const url = `/api/comptabilite/export/csv?annee=${filters.annee}`;
    fetchWithAuth(url).then(r => r.blob()).then(blob => {
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `transactions_${filters.annee}.csv`; a.click();
    });
  };

  const totalRecettes = txns.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const totalDepenses = txns.filter(t => t.amount < 0).reduce((s, t) => s + Math.abs(t.amount), 0);

  return (
    <div>
      {/* Barre d'actions */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} onKeyDown={handleSearch} placeholder="Rechercher… (Entrée)" style={{ ...is, width: 200 }} />
        <select value={filters.annee} onChange={e => setFilters(f => ({ ...f, annee: e.target.value }))} style={{ ...is, width: 110, cursor: 'pointer' }}>
          {[2024, 2025, 2026, 2027].map(y => <option key={y}>{y}</option>)}
        </select>
        <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))} style={{ ...is, width: 150, cursor: 'pointer' }}>
          <option value="">Tous types</option>
          <option value="recette">Recettes</option>
          <option value="depense">Dépenses</option>
        </select>
        <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))} style={{ ...is, width: 180, cursor: 'pointer' }}>
          <option value="">Toutes catégories</option>
          <optgroup label="Recettes">{(categories.recettes || []).map(c => <option key={c}>{c}</option>)}</optgroup>
          <optgroup label="Dépenses">{(categories.depenses || []).map(c => <option key={c}>{c}</option>)}</optgroup>
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={exportCSV} style={{ padding: '7px 14px', borderRadius: 8, background: 'transparent', border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>Export CSV</button>
        <button onClick={() => setModal('new')} style={{ padding: '7px 16px', borderRadius: 8, background: C.accent, border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', fontWeight: 600 }}>+ Saisie manuelle</button>
      </div>

      {/* Totaux */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <div style={{ background: C.green + '15', border: `1px solid ${C.green}30`, borderRadius: 8, padding: '7px 14px', fontSize: 13 }}>
          <span style={{ color: C.muted }}>Recettes </span><span style={{ color: C.green, fontWeight: 700, fontFamily: 'DM Mono, monospace' }}>{fmt(totalRecettes)}</span>
        </div>
        <div style={{ background: C.red + '15', border: `1px solid ${C.red}30`, borderRadius: 8, padding: '7px 14px', fontSize: 13 }}>
          <span style={{ color: C.muted }}>Dépenses </span><span style={{ color: C.red, fontWeight: 700, fontFamily: 'DM Mono, monospace' }}>{fmt(totalDepenses)}</span>
        </div>
        <div style={{ background: C.accent + '15', border: `1px solid ${C.accent}30`, borderRadius: 8, padding: '7px 14px', fontSize: 13 }}>
          <span style={{ color: C.muted }}>Résultat </span>
          <span style={{ color: (totalRecettes - totalDepenses) >= 0 ? C.green : C.red, fontWeight: 700, fontFamily: 'DM Mono, monospace' }}>{fmt(totalRecettes - totalDepenses)}</span>
        </div>
        <div style={{ color: C.faint, fontSize: 12, display: 'flex', alignItems: 'center', marginLeft: 8 }}>{txns.length} ligne{txns.length !== 1 ? 's' : ''}</div>
      </div>

      {/* Table */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div className="modal-scroll" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['Date', 'Libellé', 'Catégorie', 'Montant TTC', 'TVA', 'Source', ''].map(h => (
                  <th key={h} style={{ padding: '9px 14px', fontSize: 11, color: C.faint, fontWeight: 600, textTransform: 'uppercase', textAlign: h === 'Montant TTC' ? 'right' : 'left', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: C.muted }}>Chargement…</td></tr>
              ) : txns.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: C.faint }}>Aucune transaction — synchronisez Qonto ou ajoutez une saisie manuelle</td></tr>
              ) : txns.map(t => (
                <tr key={t.id} style={{ borderBottom: `1px solid ${C.border}22` }}
                  onMouseEnter={e => e.currentTarget.style.background = '#ffffff08'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '9px 14px', fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>{fmtD(t.settled_at)}</td>
                  <td style={{ padding: '9px 14px', fontSize: 13, color: C.text, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.label}
                    {t.rapprochee && <span style={{ marginLeft: 6, fontSize: 10, background: C.green + '22', color: C.green, border: `1px solid ${C.green}33`, borderRadius: 4, padding: '1px 5px' }}>rapprochée</span>}
                    {t.note && <div style={{ fontSize: 11, color: C.faint, marginTop: 1 }}>{t.note}</div>}
                  </td>
                  <td style={{ padding: '9px 14px' }}>
                    <Badge text={t.category} color={CATEG_COLORS[t.category]} />
                  </td>
                  <td style={{ padding: '9px 14px', fontSize: 14, fontWeight: 700, fontFamily: 'DM Mono, monospace', textAlign: 'right', color: t.amount >= 0 ? C.green : C.red, whiteSpace: 'nowrap' }}>
                    {t.amount >= 0 ? '+' : ''}{fmt(t.amount)}
                  </td>
                  <td style={{ padding: '9px 14px', fontSize: 11, color: C.faint, whiteSpace: 'nowrap' }}>
                    {t.vat_amount > 0 ? `${fmt(t.vat_amount)} (${t.vat_rate}%)` : '—'}
                  </td>
                  <td style={{ padding: '9px 14px', fontSize: 11, color: C.faint }}>
                    <Badge text={t.source || 'manuel'} color={t.source === 'qonto' ? C.cyan : C.muted} />
                  </td>
                  <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                    <button onClick={() => setModal(t)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 14, padding: '2px 6px' }}>✏️</button>
                    <button onClick={() => del(t.id)} style={{ background: 'none', border: 'none', color: C.red + 'aa', cursor: 'pointer', fontSize: 14, padding: '2px 6px' }}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <TxnModal
          txn={modal === 'new' ? null : modal}
          categories={categories}
          onSave={save}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

// ─── Onglet Qonto API ─────────────────────────────────────────────────────────
function TabQonto({ fetchWithAuth }) {
  const [status, setStatus]   = useState(null);
  const [result, setResult]   = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchWithAuth('/api/comptabilite/qonto/status').then(r => r.json()).then(setStatus);
  }, []);

  const doSync = async () => {
    setSyncing(true);
    setResult(null);
    const r = await fetchWithAuth('/api/comptabilite/qonto/sync', { method: 'POST' });
    const d = await r.json();
    setResult(d);
    setSyncing(false);
  };

  if (!status) return <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Chargement…</div>;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>

      {/* Statut connexion */}
      <div style={{ background: C.card, border: `1px solid ${status.configured && !status.error ? C.accent + '44' : C.border}`, borderRadius: 10, padding: '18px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: status.accounts?.length ? 14 : 0 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: status.configured && !status.error ? C.accent : C.red, flexShrink: 0 }} />
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
            {status.configured && !status.error ? 'Connecté à Qonto' : status.configured ? 'Erreur de connexion' : 'Qonto non configuré'}
          </div>
        </div>

        {status.accounts?.length > 0 && status.accounts.map(acc => (
          <div key={acc.iban} style={{ background: '#ffffff08', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{acc.name || acc.account_type || 'Compte principal'}</div>
              <div style={{ fontSize: 11, color: C.faint, fontFamily: 'DM Mono, monospace', marginTop: 2 }}>{acc.iban}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: acc.balance_cents >= 0 ? C.green : C.red, fontFamily: 'DM Mono, monospace' }}>
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: acc.currency || 'EUR' }).format((acc.balance_cents || 0) / 100)}
              </div>
              <div style={{ fontSize: 10, color: C.faint, marginTop: 1 }}>solde Qonto</div>
            </div>
          </div>
        ))}

        {status.error && (
          <div style={{ fontSize: 12, color: C.red, background: C.red + '15', border: `1px solid ${C.red}30`, borderRadius: 8, padding: '8px 12px', marginTop: 10 }}>
            {status.error}
          </div>
        )}
      </div>

      {/* Instructions si non configuré */}
      {!status.configured && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '18px 20px', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Configuration requise</div>
          <ol style={{ fontSize: 12, color: C.muted, paddingLeft: 18, lineHeight: 2, margin: 0 }}>
            <li>Dans Qonto : <strong style={{ color: C.text }}>Paramètres → Intégrations → API</strong></li>
            <li>Générer une clé API (Login + Secret Key)</li>
            <li>Renseigner dans <code style={{ background: '#ffffff10', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>backend/.env</code> :</li>
          </ol>
          <pre style={{ background: '#0d0f1a', border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', fontSize: 12, color: C.cyan, marginTop: 10, overflow: 'auto' }}>
{`QONTO_LOGIN=organisation-slug--secret-id
QONTO_SECRET=votre-secret-key`}
          </pre>
          <div style={{ fontSize: 11, color: C.faint, marginTop: 10 }}>Redémarrez le serveur backend après avoir renseigné ces variables.</div>
        </div>
      )}

      {/* Bouton sync */}
      {status.configured && !status.error && (
        <button
          onClick={doSync}
          disabled={syncing}
          style={{ width: '100%', padding: '13px 0', borderRadius: 10, background: syncing ? C.border : C.accent, border: 'none', color: syncing ? C.muted : '#fff', cursor: syncing ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 15, fontFamily: 'inherit', transition: 'all 0.2s', marginBottom: 20 }}
        >
          {syncing ? 'Synchronisation en cours…' : 'Synchroniser les transactions Qonto'}
        </button>
      )}

      {/* Résultat */}
      {result && (
        <div style={{ background: C.card, border: `1px solid ${result.error ? C.red : C.accent}44`, borderRadius: 10, padding: '20px 24px', textAlign: 'center' }}>
          {result.error ? (
            <>
              <div style={{ fontSize: 32, marginBottom: 10 }}>❌</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.red, marginBottom: 6 }}>Erreur de synchronisation</div>
              <div style={{ fontSize: 12, color: C.muted }}>{result.error}</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 32, marginBottom: 10 }}>{result.inserted > 0 ? '✅' : 'ℹ️'}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 16 }}>Synchronisation terminée</div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <KPI label="Nouvelles"      value={result.inserted} color={C.green}  icon="✅" />
                <KPI label="Déjà présentes" value={result.skipped}  color={C.orange} icon="⏭" />
                <KPI label="Total traité"   value={result.total}    color={C.muted}  icon="📊" />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Onglet Prévisionnel ──────────────────────────────────────────────────────
function TabPrevisionnel({ fetchWithAuth }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchWithAuth('/api/comptabilite/previsionnel').then(r => r.json()).then(setData);
  }, []);

  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Chargement…</div>;

  return (
    <div>
      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <KPI icon="🔄" label="MRR (contrats récurrents)" value={fmt(data.mrr)} color={C.green} sub="Revenu mensuel garanti" />
        <KPI icon="🎯" label="Pipeline pondéré" value={fmt(data.pipeline_pondere)} color={C.blue} sub="Opps × probabilité" />
        <KPI icon="📉" label="Charges moy. 3 mois" value={fmt(data.avg_charges)} color={C.red} sub="Base des projections" />
        <KPI icon="🏦" label="Trésorerie actuelle" value={fmt(data.tresorerie_actuelle)} color={data.tresorerie_actuelle >= 0 ? C.green : C.red} sub="Cumul transactions" />
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '18px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Projection trésorerie — 6 mois</div>
          <div style={{ fontSize: 11, color: C.faint }}>Basé sur MRR + pipeline pondéré mensuel et charges moyennes</div>
        </div>
        <div style={{ height: 4 }} />
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data.projection}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="label" tick={{ fill: C.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: C.muted, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${Math.round(v / 1000)}k`} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, color: C.muted }} />
            <Area type="monotone" dataKey="recettes" name="Recettes" stroke={C.green} fill={C.green + '22'} strokeWidth={2} />
            <Area type="monotone" dataKey="depenses" name="Dépenses" stroke={C.red}   fill={C.red   + '22'} strokeWidth={2} />
            <Area type="monotone" dataKey="solde"    name="Solde"    stroke={C.accent} fill={C.accent + '18'} strokeWidth={2} strokeDasharray="5 3" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {['Mois', 'Recettes prévisionnelles', 'Dépenses prévisionnelles', 'Solde cumulé'].map(h => (
                <th key={h} style={{ padding: '10px 16px', fontSize: 11, color: C.faint, fontWeight: 600, textTransform: 'uppercase', textAlign: h === 'Mois' ? 'left' : 'right' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.projection.map((p, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}22` }}>
                <td style={{ padding: '10px 16px', fontSize: 13, color: C.text, fontWeight: 500 }}>{p.label}</td>
                <td style={{ padding: '10px 16px', fontSize: 13, color: C.green, fontWeight: 600, textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{fmt(p.recettes)}</td>
                <td style={{ padding: '10px 16px', fontSize: 13, color: C.red,   fontWeight: 600, textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{fmt(p.depenses)}</td>
                <td style={{ padding: '10px 16px', fontSize: 14, fontWeight: 700, textAlign: 'right', fontFamily: 'DM Mono, monospace', color: p.solde >= 0 ? C.green : C.red }}>{fmt(p.solde)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: C.faint, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px' }}>
        <strong style={{ color: C.muted }}>Hypothèses :</strong> Recettes = MRR contrats actifs + pipeline pondéré (opps en cours × probabilité) réparti sur 6 mois.
        Dépenses = moyenne des 3 derniers mois de transactions. Ajustez les données de base pour affiner la projection.
      </div>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
const TABS = [
  { id: 'dashboard',    label: 'Dashboard'     },
  { id: 'transactions', label: 'Transactions'  },
  { id: 'qonto',        label: 'Sync Qonto'    },
  { id: 'previsionnel', label: 'Prévisionnel'  },
];

export default function ComptaPage() {
  const fetchWithAuth = useAuthFetch();
  const [tab, setTab] = useState('dashboard');

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}>Comptabilité & Finances</h1>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Suivi financier, sync Qonto, rapprochement factures, prévisionnel trésorerie</div>
      </div>

      <div style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${C.border}`, marginBottom: 24 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '9px 18px', borderRadius: '8px 8px 0 0', border: 'none',
            background: tab === t.id ? C.accent + '20' : 'transparent',
            color: tab === t.id ? C.accent : C.muted,
            fontWeight: tab === t.id ? 700 : 400, fontSize: 13,
            cursor: 'pointer', fontFamily: 'inherit',
            borderBottom: tab === t.id ? `2px solid ${C.accent}` : '2px solid transparent',
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'dashboard'    && <TabDashboard    fetchWithAuth={fetchWithAuth} />}
      {tab === 'transactions' && <TabTransactions fetchWithAuth={fetchWithAuth} />}
      {tab === 'qonto'        && <TabQonto        fetchWithAuth={fetchWithAuth} />}
      {tab === 'previsionnel' && <TabPrevisionnel fetchWithAuth={fetchWithAuth} />}
    </div>
  );
}
